<script setup lang="ts">
import {onBeforeUnmount, onMounted, provide, ref} from 'vue';
import HeaderLogo from '@/vue/organisms/header/header-logo.vue';
import LanguageSwitcher from '@/vue/molecules/language-switcher.vue';
import PartnerMediaPanel from '@/vue/molecules/partner-media-panel.vue';
import signUpBackground from '@/assets/img/sign-up-background.webp';
import signUpBackgroundMobile from '@/assets/img/sign-up-background-mobile.webp';
import type {NavigationItem} from '@/ts/types/component/navigation';
import {usePartnerBranding} from '@/ts/composables/stateful/use-partner-branding';
import {authCardOverlaysKey} from '@/ts/composables/pure/use-auth-card-overlays';

defineProps<{
  footerItems?: NavigationItem[];
}>();

defineEmits<{
  clickFooterItem: [string],
}>();

const {partner} = usePartnerBranding();

// Collapsing partner banner (phones/tablets): the pinning itself is pure CSS
// (sticky with a negative top on .auth-card__media); this handler only feeds
// the caption fade with the scroll progress (0 = full banner, 1 = pinned strip).
const BANNER_MIN_HEIGHT = 88;
const cardRef = ref<HTMLElement | null>(null);

// The banner is pinned by this template, so this template is what knows where it is. Handed to
// whatever the card holds rather than left to be found by class name from the outside.
provide(authCardOverlaysKey, {
  banner: () => {
    const media = cardRef.value?.querySelector<HTMLElement>('.auth-card__media') ?? null;
    return media && window.getComputedStyle(media).position === 'sticky' ? media : null;
  },
});

function onCardScroll(): void {
  const card = cardRef.value;
  const media = card?.querySelector<HTMLElement>('.auth-card__media');
  if (!card || !media) {
    return;
  }
  // Approximation: the CSS pin travels the card's top padding further (see the
  // sticky top calc), but the caption is fully faded from progress 0.5 anyway.
  const collapseDistance = media.offsetHeight - BANNER_MIN_HEIGHT;
  const progress = collapseDistance > 0 ? Math.min(card.scrollTop / collapseDistance, 1) : 0;
  card.style.setProperty('--banner-collapse', `${progress}`);
}

onMounted(() => {
  cardRef.value?.addEventListener('scroll', onCardScroll, {passive: true});
});

onBeforeUnmount(() => {
  cardRef.value?.removeEventListener('scroll', onCardScroll);
});
</script>
<template>
  <div
    class="public-auth-template"
    :style="{
      '--auth-bg': `url(${signUpBackground})`,
      '--auth-bg-mobile': `url(${signUpBackgroundMobile})`,
    }"
  >
    <header class="auth-header">
      <header-logo/>
      <language-switcher/>
    </header>
    <main class="auth-main">
      <div
        ref="cardRef"
        class="auth-card"
        :class="{'auth-card--split': !!partner}"
        :style="{'--banner-min': `${BANNER_MIN_HEIGHT}px`}"
      >
        <div class="auth-card__form">
          <slot/>
        </div>
        <partner-media-panel v-if="partner" :partner="partner" class="auth-card__media"/>
      </div>
    </main>
    <footer class="auth-footer">
      <div class="footer-pill">
        <button
          v-for="item in footerItems"
          :key="item.id"
          type="button"
          class="footer-link"
          @click="$emit('clickFooterItem', item.id)"
        >
          {{ $t(item.labelKey) }}
        </button>
      </div>
    </footer>
  </div>
</template>
<style scoped lang="scss">
.public-auth-template {
  position: relative;
  z-index: 0;
  width: 100%;
  // Lock to the viewport: the header stays at the top and the footer at the
  // bottom; only the middle area scrolls (see .auth-main). 100dvh tracks mobile
  // browser chrome; 100vh is the fallback for browsers without dynamic-viewport
  // units.
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.public-auth-template::before {
  content: '';
  position: fixed;
  inset: 0;
  z-index: -1;
  background-color: var(--Neutral-UI-neutral-ui-20);
  background-image: var(--auth-bg);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
}

@media (orientation: portrait) {
  .public-auth-template::before {
    background-image: var(--auth-bg-mobile);
  }
}

.auth-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--Spacing-spacing-5) var(--Spacing-spacing-6);
  flex-shrink: 0;
}

.auth-main {
  flex: 1;
  // min-height:0 lets this flex child shrink below its content so the card's
  // max-height:100% resolves — the CARD is the scroll container, this middle
  // area never scrolls itself.
  min-height: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0 var(--Spacing-spacing-5);
}

.auth-card {
  width: 100%;
  max-width: 560px;
  // ui-0 (not ui-10): the design's card surface — lightest in light scheme,
  // darkest in dark — and what the dropdowns' ui-10 option rows contrast with.
  background-color: var(--Neutral-UI-neutral-ui-0);
  border-radius: var(--card-border-radius);
  padding: var(--Spacing-spacing-6) var(--Spacing-spacing-6) 0;
  box-shadow: 0 8px 40px rgb(0 0 0 / 8%);
  display: flex;
  flex-direction: column;
  // The card is the scroll container at EVERY size (capped to the middle
  // area's height): the scrollbar renders inside the card instead of at the
  // page edge, and the sticky CTA bar pins flush to the card's scrollport.
  // The x-axis stays clipped for the full-bleed banner corners.
  max-height: 100%;
  overflow-x: clip;
  overflow-y: auto;
  // Auto margins center the card vertically when it fits and collapse to zero
  // when it overflows, so a tall form starts at the top and scrolls naturally
  // (a plain `align-items: center` would clip the overflowing top instead).
  margin-top: auto;
  margin-bottom: auto;

  // Desktop: additionally cap at 720px on tall screens.
  @include screen-lg {
    max-height: min(720px, 100%);
  }
}

@include screen-sm {
  .auth-card {
    padding: var(--Spacing-spacing-7) var(--Spacing-spacing-7) 0;
  }
}

// Default: the form wrapper is layout-transparent so the non-partner card stays
// pixel-identical to the single-column layout; the media panel only renders for
// partner-branded sign-ups.
.auth-card__form {
  display: contents;
}

// Partner banner (phones + tablets): first element inside the card, bleeding
// to the card edges via negative margins against the card padding. Collapsing
// header: the sticky negative top lets the banner scroll away with the form
// until only a minimal strip (--banner-min — the logo over the photo's darkest
// area) remains pinned to the card's top edge while the form scrolls beneath.
// The caption fades out on the way (--banner-collapse, fed by the card's
// scroll handler) so it never gets clipped mid-line.
.auth-card__media {
  --banner-full: 220px;

  order: -1;
  flex-shrink: 0;
  height: var(--banner-full);
  position: sticky;
  // The extra subtrahend compensates the card's top padding: the sticky inset
  // resolves from the content edge (inside the padding), 24/32px below the
  // card's visible top where the strip must pin.
  top: calc(var(--banner-min, 88px) - var(--banner-full) - var(--Spacing-spacing-6));
  z-index: 1;
  margin:
    calc(-1 * var(--Spacing-spacing-6))
    calc(-1 * var(--Spacing-spacing-6))
    var(--Spacing-spacing-6);

  :deep(.partnership-text) {
    // The multiplier empties it halfway through the collapse; calc() output
    // outside [0,1] clamps per the CSS spec.
    opacity: calc(1 - var(--banner-collapse, 0) * 2);
  }
}

@include screen-sm {
  .auth-card__media {
    // 348px matches the Figma tablet banner (600x348).
    --banner-full: 348px;

    top: calc(var(--banner-min, 88px) - var(--banner-full) - var(--Spacing-spacing-7));
    margin:
      calc(-1 * var(--Spacing-spacing-7))
      calc(-1 * var(--Spacing-spacing-7))
      var(--Spacing-spacing-7);
  }
}

// Desktop: the card becomes a two-column shell (form + media panel) per the
// Allianz sign-up design; the card's padding/scroll move to the form column so
// the panel bleeds to the card edge and stays fixed while the form scrolls.
@include screen-lg {
  .auth-card--split {
    // 1288px matches the Figma card at 1728px viewport; the 86% floor keeps
    // proportional side margins on smaller desktops instead of edge-to-edge.
    max-width: min(1288px, 86%);
    // Fill the available height (like the design) so the media panel has
    // presence instead of collapsing to the form's content height.
    height: min(720px, 100%);
    padding: 0;
    flex-direction: row;
    overflow: hidden;

    // The form keeps its design width and the decorative photo absorbs the
    // squeeze on narrow desktops (at the 1288px card both land on the Figma
    // 600/688 split; a fixed photo basis crushed the form to ~290px at 1280).
    .auth-card__form {
      display: flex;
      flex-direction: column;
      flex: 0 1 600px;
      min-width: 360px;
      padding: var(--Spacing-spacing-7) var(--Spacing-spacing-7) 0;
      overflow-y: auto;
    }

    .auth-card__media {
      order: 0;
      flex: 1 1 auto;
      height: auto;
      min-width: 0;
      margin: 0;
      // The side panel never collapses: the form column scrolls, not the card.
      position: static;

      // A resize across this breakpoint can leave a stale --banner-collapse
      // from the phone/tablet layout (the card no longer scrolls here, so the
      // handler never resets it) — the panel caption is always fully visible.
      :deep(.partnership-text) {
        opacity: 1;
      }
    }
  }
}

// The wizard's scroll containers keep an always-visible classic scrollbar
// (the app-wide auto-hide would leave overflow undiscoverable after window
// resizes, and macOS overlay bars only paint during a scroll — reverting the
// standard properties to `auto` makes Chrome honor the ::-webkit styling).
// Thin and track-less so the reserved gutter next to the full-bleed banner
// stays discreet.
.auth-card,
.auth-card__form {
  scrollbar-color: auto;
  scrollbar-width: auto;

  &::-webkit-scrollbar {
    width: 7px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: var(--Neutral-UI-neutral-ui-30);
  }
}

.auth-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  // Phones: the footer is a full-width bar flush to the bottom edge (per the
  // mobile design); the floating pill treatment starts at tablet.
  padding: 0;
  flex-shrink: 0;

  @include screen-sm {
    padding: var(--Spacing-spacing-3) var(--Spacing-spacing-6) var(--Spacing-spacing-6);
  }
}

.footer-pill {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  // Phones: links spread across the full-width bottom bar.
  justify-content: space-evenly;
  text-align: center;
  gap: var(--Spacing-spacing-1);
  width: 100%;
  background-color: var(--Neutral-UI-neutral-ui-20);
  padding: var(--Spacing-spacing-3) var(--Spacing-spacing-2);
}

// Tablet up: centered fully-rounded pill. Fixed paddings: the responsive
// spacing tokens double at desktop, blowing the pill up to twice the height
// the design specs.
@include screen-sm {
  .footer-pill {
    justify-content: center;
    width: auto;
    max-width: min(16rem, 100%);
    gap: var(--Spacing-spacing-1) var(--Spacing-spacing-3);
    border-radius: var(--button-border-radius);
    padding: 6px 4px;
  }
}

@include screen-md {
  .footer-pill {
    max-width: none;
    gap: var(--Spacing-spacing-2);
    padding: 6px 12px;
  }
}

.footer-link {
  @include font-body-sm;
  @include focus-visible;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--Neutral-UI-neutral-ui-100);
  padding: 8px;

  &:hover {
    text-decoration: underline;
  }
}
</style>
